import React, { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { I } from '../icons-ds';
import { Button, Tag } from '../ui';
import { useViewport } from '../layout';

type Slide = {
  icon: ReactNode;
  title: string;
  body: string;
  art: ReactNode;
};

function buildSlides(): Slide[] {
  return [
    {
      icon: <I.search size={32} />,
      title: 'Find any phrase in seconds',
      body:
        'Type a keyword, dot trigger, or condition. Results rank by usage and recency so what you reach for most is always first.',
      art: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 24 }}>
          <div
            style={{
              background: 'var(--bg-surface)', borderRadius: 12, padding: '12px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
              boxShadow: 'var(--shadow-2)', border: '1px solid var(--line-divider)',
            }}
          >
            <I.search size={18} style={{ color: 'var(--fg-tertiary)' }} />
            <span style={{ color: 'var(--fg-primary)', fontSize: 15 }}>hypertension</span>
            <span
              style={{
                marginLeft: 'auto', width: 2, height: 16,
                background: 'var(--accent)', animation: 'dpBlink 1s steps(2) infinite',
              }}
            />
          </div>
          {[
            { tag: 'inpatient' as const, title: 'Hypertension (inpatient)', trigger: '.htninp', label: 'Inpatient' },
            { tag: 'outpatient' as const, title: 'Hypertension (outpatient)', trigger: '.htnop', label: 'Outpatient' },
            { tag: 'ed' as const, title: 'Hypertensive emergency', trigger: '.htnemr', label: 'ED' },
          ].map((r, i) => (
            <div
              key={r.trigger}
              style={{
                background: 'var(--bg-surface)', borderRadius: 12, padding: '10px 12px',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: 'var(--shadow-1)', border: '1px solid var(--line-divider)',
                animation: `dpSlideUp ${0.3 + i * 0.1}s var(--ease-out) both`,
              }}
            >
              <Tag kind={r.tag} style={{ flexShrink: 0 }}>{r.label}</Tag>
              <span style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{r.title}</span>
              <span className="t-mono" style={{ color: 'var(--fg-tertiary)' }}>{r.trigger}</span>
            </div>
          ))}
          <style>{`@keyframes dpBlink { 50% { opacity: 0; } }`}</style>
        </div>
      ),
    },
    {
      icon: <I.copy size={32} />,
      title: 'One tap, plain-text out',
      body:
        'Every section copies cleanly. No carried-over fonts or invisible characters that break formatting in your EMR.',
      art: (
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: 'var(--bg-surface)', borderRadius: 14, padding: 16,
              boxShadow: 'var(--shadow-2)', border: '1px solid var(--line-divider)',
              position: 'relative',
            }}
          >
            <div
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Discharge plan</span>
              <Button size="sm" variant="primary" icon={<I.check size={14} stroke={2.5} />}>Copied</Button>
            </div>
            <div className="t-mono" style={{ color: 'var(--fg-secondary)', fontSize: 12, lineHeight: 1.65 }}>
              1. ARNI 24/26 mg BID<br />
              2. Carvedilol 3.125 mg BID<br />
              3. Spironolactone 25 mg<br />
              4. Dapagliflozin 10 mg
            </div>
          </div>
          <div
            style={{
              background: 'var(--accent-fill)', padding: 10, borderRadius: 12,
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--accent)', fontSize: 13, fontWeight: 500,
            }}
          >
            <I.check size={16} stroke={2.4} /> Pasted as clean text into Epic
          </div>
        </div>
      ),
    },
  ];
}

export function OnboardingScreen() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = viewport === 'mobile';

  const slides = buildSlides();
  const stepParam = parseInt(params.step || '1', 10);
  const step = Number.isFinite(stepParam) ? Math.min(Math.max(stepParam - 1, 0), slides.length - 1) : 0;
  const s = slides[step];

  const finish = () => {
    localStorage.setItem('dp-onboarded', 'true');
    navigate('/search');
  };

  const onNext = () => {
    if (step < slides.length - 1) {
      navigate('/onboarding/' + (step + 2));
    } else {
      finish();
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh', background: 'var(--bg-canvas)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="plain" size="sm" onClick={finish}>Skip</Button>
      </div>

      <div
        style={{
          flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', justifyContent: 'center',
          gap: isMobile ? 24 : 56, padding: '0 24px',
          maxWidth: 1100, margin: '0 auto', width: '100%',
        }}
      >
        <div
          style={{
            flex: isMobile ? '0 0 auto' : 1, width: isMobile ? '100%' : 'auto',
            maxWidth: 420, aspectRatio: isMobile ? '4 / 3' : 'auto',
            background: 'var(--bg-grouped)', borderRadius: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', minHeight: isMobile ? 240 : 360,
          }}
        >
          {s.art}
        </div>

        <div style={{ flex: 1, maxWidth: 420 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'var(--accent-fill)', color: 'var(--accent)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 24,
            }}
          >
            {s.icon}
          </div>
          <div className="t-largetitle" style={{ marginBottom: 12 }}>{s.title}</div>
          <div className="t-callout" style={{ color: 'var(--fg-secondary)' }}>{s.body}</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 24, maxWidth: 1100, margin: '0 auto', width: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {slides.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === step ? 24 : 6, height: 6, borderRadius: 999,
                background: i === step ? 'var(--accent)' : 'var(--bg-fill-2)',
                transition: 'all var(--dur-base) var(--ease-out)',
              }}
            />
          ))}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          iconRight={step < slides.length - 1 ? <I.arrowR size={18} /> : undefined}
        >
          {step < slides.length - 1 ? 'Continue' : 'Get started'}
        </Button>
      </div>
    </div>
  );
}

export default OnboardingScreen;
